// /notificacoes, full notification list for the logged-in user

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Bell } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../hooks/useAuth';
import { EmptyState } from '../components/ui/EmptyState';

interface Notification {
  id: string;
  title: string;
  body: string;
  link: string | null;
  read: boolean;
  created_at: string;
}

export function NotificationsPage() {
  const { profile }           = useAuth();
  const [items,   setItems]   = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!profile?.id) return;
    load();
  }, [profile?.id]);

  async function load() {
    setLoading(true);
    const { data } = await supabase
      .from('user_notifications')
      .select('id, title, body, link, read, created_at')
      .eq('user_id', profile!.id)
      .order('created_at', { ascending: false })
      .limit(50);

    const rows = (data ?? []) as Notification[];
    setItems(rows);
    setLoading(false);

    // Mark all unread as read
    const unreadIds = rows.filter(n => !n.read).map(n => n.id);
    if (unreadIds.length > 0) {
      await supabase.from('user_notifications').update({ read: true }).in('id', unreadIds);
    }
  }

  return (
    <div className="page-container">
      <div style={{ marginBottom: '1.5rem' }}>
        <h1 className="font-display" style={{ fontSize: 'clamp(1.375rem,4vw,1.875rem)', color: 'var(--text-primary)', marginBottom: '0.25rem' }}>
          Notificações
        </h1>
        <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-label)' }}>
          Todas as suas notificações e atualizações.
        </p>
      </div>

      {loading && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {[1, 2, 3].map(i => (
            <div key={i} className="card shimmer" style={{ height: '80px' }} />
          ))}
        </div>
      )}

      {!loading && items.length === 0 && (
        <EmptyState
          emoji="🔔"
          title="Sem notificações"
          subtitle="Você não tem notificações por enquanto. Novas atualizações aparecerão aqui."
        />
      )}

      {!loading && items.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {items.map(n => {
            const content = (
              <div
                key={n.id}
                className="card"
                style={{
                  padding: '0.875rem 1rem',
                  backgroundColor: n.read ? 'var(--bg-surface)' : 'rgba(31,78,95,0.04)',
                  borderColor: n.read ? 'var(--border-subtle)' : 'rgba(31,78,95,0.22)',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
                  <div style={{
                    width: '32px', height: '32px', borderRadius: '50%', flexShrink: 0,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    backgroundColor: n.read ? 'var(--bg-elevated)' : 'rgba(245,200,66,0.12)',
                  }}>
                    <Bell size={15} style={{ color: n.read ? 'var(--text-muted)' : '#F5C842' }} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.2rem' }}>
                      {!n.read && (
                        <span style={{
                          width: '6px', height: '6px', borderRadius: '50%',
                          backgroundColor: '#F5C842', flexShrink: 0,
                        }} />
                      )}
                      <p style={{ fontWeight: 600, color: 'var(--text-primary)', margin: 0, fontSize: 'var(--text-label)' }}>
                        {n.title}
                      </p>
                    </div>
                    <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-label)', margin: '0 0 0.25rem', lineHeight: 1.5 }}>
                      {n.body}
                    </p>
                    <p style={{ color: 'var(--text-muted)', fontSize: 'var(--text-caption)', margin: 0 }}>
                      {new Date(n.created_at).toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' })}
                      {' '}·{' '}
                      {new Date(n.created_at).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                </div>
              </div>
            );

            return n.link ? (
              <Link key={n.id} to={n.link} style={{ textDecoration: 'none' }}>
                {content}
              </Link>
            ) : (
              <div key={n.id}>{content}</div>
            );
          })}
        </div>
      )}
    </div>
  );
}
